import * as React from "react";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Button, Divider, Pagination, PaginationItem } from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import API from "../../Api/api";
import toast from "react-hot-toast";
import { message } from "antd";
import moment from "moment";

const columns = [
  { field: "asset_pool", headerName: "ASSET POOL", minWidth: 250 },
  {
    field: "cost",
    headerName: "COST",
    minWidth: 170,
    align: "left",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "accum_depreciation",
    headerName: "TOTAL AMORTIZATION",
    minWidth: 170,
    align: "left",
    format: (value) => value.toLocaleString("en-US"),
  },
  {
    field: "carrying_amount",
    headerName: "CARRYING AMOUNT",
    minWidth: 170,
    align: "left",
  },
  {
    field: "depreciation",
    headerName: "AMORTIZATION",
    minWidth: 170,
    align: "left",
  },
];

export default function IntangibleAssetSummaryReport() {
  const mode_state = useSelector((state) => state.mode);
  const [mode, setMode] = React.useState(mode_state);
  React.useEffect(() => {
    setMode(mode_state);
  }, [mode_state]);
  function createData(
    asset_pool,
    cost,
    accum_depreciation,
    carrying_amount,
    depreciation
  ) {
    return {
      asset_pool,
      cost,
      accum_depreciation,
      carrying_amount,
      depreciation,
    };
  }
  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color='primary'
        sx={{ bgcolor: "#ffffff" }}
        variant='outlined'
        shape='rounded'
        page={page + 1}
        count={pageCount}
        // @ts-expect-error
        renderItem={(props2) => <PaginationItem {...props2} disableRipple />}
        onChange={(event, value) => apiRef.current.setPage(value - 1)}
      />
    );
  }

  const assetsData = useSelector((state) => state.intangible);
  const locationData = useSelector((state) => state.location);
  const [data, setData] = React.useState(assetsData);
  const [filterData, setFilterData] = React.useState({
    location: "",
    category: "",
    depreciation_start: "",
    depreciation_end: "",
    acquisition_start: "",
    acquisition_end: "",
    expiration_start: "",
    expiration_end: "",
  });
  function clearFilter() {
    setFilterData({
      location: "",
      category: "",
      depreciation_start: "",
      depreciation_end: "",
      acquisition_start: "",
      acquisition_end: "",
      expiration_start: "",
      expiration_end: "",
    });
    setData(assetsData);
  }
  //  we will push all cost,accum_depreciation,depreciation and carrying ammount
  // !------------furniture and fittings------------
  let intangiblesTotalCost = [];
  let intangiblesTotalDepreciation = [];
  let intangiblesTotalCarryingAmount = [];
  let intangiblesTotalAccumDepn = [];

  let totalCost = [];
  let totalAccumDepn = [];
  let totalDepn = [];
  let totalCarryingAmount = [];

  const location = filterData.location
    ? `&location=${filterData.location.toLocaleLowerCase()}`
    : "";

  const purchase_date_start = filterData.acquisition_start
    ? `&purchase_date[gte]=${filterData.acquisition_start}`
    : "";
  const purchase_date_end = filterData.acquisition_end
    ? `&purchase_date[lte]=${filterData.acquisition_end}`
    : "";
  const expiration_start = filterData.expiration_start
    ? `&exhaustion_date[gte]=${filterData.expiration_start}`
    : "";
  const expiration_end = filterData.expiration_end
    ? `&exhaustion_date[lte]=${filterData.expiration_end}`
    : "";
  const depreciation_end = filterData.depreciation_end
    ? `&depreciation_end=${filterData.depreciation_end}`
    : "";
  const depreciation_start = filterData.depreciation_start
    ? `&depreciation_start=${filterData.depreciation_start}`
    : "";

  const query = `/api/v1/company-assets/get-asset-by-code/code?disposed=false&approved=true&category[eq]=intangibles${location}${purchase_date_end}${purchase_date_start}${expiration_start}${expiration_end}${depreciation_end}${depreciation_start}`;

  // ! A function to fetch queried data from the database//---------start------------
  const fetchAsset = async () => {
    let fetchingId;
    try {
      if (
        !location &&
        !purchase_date_end &&
        !purchase_date_start &&
        !expiration_end &&
        !expiration_start &&
        !depreciation_end &&
        !depreciation_start
      ) {
        toast.error("you can't generate  a report while all fields are empty");
        return;
      }
      if (depreciation_end && !depreciation_start) {
        toast.error(
          "both depreciation end and depreciation start are required"
        );
        return;
      }
      if (!depreciation_end && depreciation_start) {
        toast.error(
          "both depreciation end and depreciation start are required"
        );
        return;
      }
      if (moment(depreciation_end) < moment(depreciation_start)) {
        toast.error(
          "depreciation end date can't be less than depreciation start date"
        );
        return;
      }
      fetchingId = toast.loading("generating report please wait...");
      const res = await API.get(query);
      toast.remove(fetchingId);
      if (res.data.length === 0) {
        message.info("No data was found");
        return;
      }
      if (res.data.length > 0) {
        setData(res.data);
        message.info("Reported successfully generated");
      }
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };
  // ! -------------function end-----------------------------

  function analyzeData(
    category,
    purchase_date,
    exhaustion_date,
    cost,
    daily_depreciation,
    days_expired,
    total_asset_life,
    days_unexpired,
    accum_depre,
    carrying_amount,
    depreciation,
    status,
    revalued,
    new_cost,
    original_cost,
    original_daily_depreciation,
    new_daily_depreciation,
    revaluation_date,
    disposed,
    disposal_date
  ) {
    if (category === "intangibles") {
      intangiblesTotalCost.push(cost);
      intangiblesTotalAccumDepn.push(accum_depre);
      intangiblesTotalCarryingAmount.push(carrying_amount);
      intangiblesTotalDepreciation.push(depreciation);
    }

    totalCost.push(cost);
    totalDepn.push(depreciation);
    totalAccumDepn.push(accum_depre);
    totalCarryingAmount.push(carrying_amount);
    return {
      category,
      purchase_date,
      exhaustion_date,
      cost,
      daily_depreciation,
      days_expired,
      total_asset_life,
      days_unexpired,
      accum_depre,
      carrying_amount,
      depreciation,
      status,
      revalued,
      new_cost,
      original_cost,
      original_daily_depreciation,
      new_daily_depreciation,
      revaluation_date,
      disposed,
      disposal_date,
    };
  }

  const handleChangeCategory = (event) => {
    setFilterData({
      ...filterData,
      category: event.target.value,
    });
  };
  const handleChangeLocation = (event) => {
    setFilterData({
      ...filterData,
      location: event.target.value,
    });
  };
  data?.map((data) => {
    return analyzeData(
      data.category,
      data.purchase_date,
      data.exhaustion_date,
      data.cost,
      data.daily_depreciation,
      data.days_expired,
      data.total_asset_life,
      data.days_unexpired,
      data.accum_depre,
      data.carrying_amount,
      data.depreciation,
      data.status,
      data.revalued,
      data.new_cost,
      data.cost,
      data.daily_depreciation,
      data.new_daily_depreciation,
      data.revaluation_date,
      data.disposed,
      data.disposal_date
    );
  });
  let currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
  });

  let newFfTotalCost = intangiblesTotalCost.reduce((acc, val) => acc + val, 0);
  let newFfTotalDepn = intangiblesTotalDepreciation.reduce(
    (acc, val) => acc + val,
    0
  );
  let newFfTotalCarryingAmount = intangiblesTotalCarryingAmount.reduce(
    (acc, val) => acc + val,
    0
  );
  let newFfTotalAccumDepn = intangiblesTotalAccumDepn.reduce(
    (acc, val) => acc + val,
    0
  );

  // ! summarization
  let allCost = totalCost.reduce((acc, val) => acc + val, 0);
  let allAccumDepn = totalAccumDepn.reduce((acc, val) => acc + val, 0);
  let allDepreciation = totalDepn.reduce((acc, val) => acc + val, 0);
  let allCarryingAmount = totalCarryingAmount.reduce(
    (acc, val) => acc + val,
    0
  );
  const rows = [
    createData(
      "Intangibles",
      currencyFormatter.format(newFfTotalCost),
      currencyFormatter.format(newFfTotalAccumDepn),
      currencyFormatter.format(newFfTotalCarryingAmount),
      currencyFormatter.format(newFfTotalDepn)
    ),

    createData(
      "Total",
      currencyFormatter.format(allCost),
      currencyFormatter.format(allAccumDepn),
      currencyFormatter.format(allCarryingAmount),
      currencyFormatter.format(allDepreciation)
    ),
  ];
  return (
    <div style={{ height: "81vh" }}>
      <div
        elevation={16}
        style={{
          fontSize: "14px",
          marginBottom: "20px",
          display: "flex",
          flexDirection: "row",
          // alignItems: "center",
          justifyContent: "space-between",
          height: "95px",
        }}>
        <div style={{ display: "flex", flexDirection: "column", rowGap: 9 }}>
          <FormControl size='small' sx={{ m: 1, width: 140 }}>
            <InputLabel
              style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}
              id='demo-simple-select-standard-label'>
              Location
            </InputLabel>
            <Select
              style={{ backgroundColor: "#ccc" }}
              labelId='demo-simple-select-standard-label'
              id='demo-simple-select-standard'
              value={filterData.location}
              onChange={handleChangeLocation}
              label='Category'>
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {locationData?.map((data) => {
                return (
                  <MenuItem key={data.id} value={data.name}>
                    {data.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size='small' sx={{ m: 1, width: 140 }}>
            <InputLabel
              style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}
              id='demo-simple-select-standard-label'>
              Category
            </InputLabel>
            <Select
              style={{ backgroundColor: "#ccc" }}
              labelId='demo-simple-select-standard-label'
              id='demo-simple-select-standard'
              value={filterData.category}
              onChange={handleChangeCategory}
              disabled
              label='Category'>
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>

              <MenuItem value={"intangibles"}>INTANGIBLES</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor='start-date'>
            Depreciation start
          </label>
          <input
            value={filterData.depreciation_start}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                depreciation_start: e.target.value,
              })
            }
            style={{
              borderRadius: "5px",
              // backgroundColor: "#cccc",
              border: "1px solid #3D4F58",
            }}
            id='start-date'
            type='date'
            size={"large"}
            name='start-date'
          />

          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor='end-date'>
            Depreciation end
          </label>
          <input
            value={filterData.depreciation_end}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                depreciation_end: e.target.value,
              })
            }
            style={{
              borderRadius: "5px",
              // backgroundColor: "#cccc",
              border: "1px solid #3D4F58",
            }}
            id='end-date'
            type='date'
            size='small'
            name='end-date'
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor='acq-start-date'>
            Acquisition start
          </label>
          <input
            value={filterData.acquisition_start}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                acquisition_start: e.target.value,
              })
            }
            style={{
              borderRadius: "5px",
              // backgroundColor: "#cccc",
              border: "1px solid #3D4F58",
            }}
            id='acq-start-date'
            type='date'
            size='small'
          />
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor='acq-start-date'>
            Acquisition end
          </label>
          <input
            value={filterData.acquisition_end}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                acquisition_end: e.target.value,
              })
            }
            style={{
              borderRadius: "5px",
              // backgroundColor: "#cccc",
              border: "1px solid #3D4F58",
            }}
            id='acq-end-date'
            type='date'
            size='small'
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor='acq-start-date'>
            Expiration start
          </label>
          <input
            value={filterData.expiration_start}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                expiration_start: e.target.value,
              })
            }
            style={{
              borderRadius: "5px",
              // backgroundColor: "#cccc",
              border: "1px solid #3D4F58",
            }}
            id='acq-end-date'
            type='date'
            size='small'
          />
          <label
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              color: `${mode === "dark" ? "#0883FF" : "#116149"}`,
            }}
            htmlFor='acq-start-date'>
            Expiration end
          </label>
          <input
            value={filterData.expiration_end}
            onChange={(e) =>
              setFilterData({
                ...filterData,
                expiration_end: e.target.value,
              })
            }
            style={{
              borderRadius: "5px",
              // backgroundColor: "#cccc",
              border: "1px solid #3D4F58",
            }}
            id='acq-end-date'
            type='date'
            size='small'
          />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}>
        <Button
          onClick={fetchAsset}
          size='small'
          variant='contained'
          style={{
            backgroundColor: `${mode === "dark" ? "#0883FF" : "#116149"}`,
          }}>
          Generate Report
        </Button>
        <Button
          onClick={clearFilter}
          size='small'
          variant='contained'
          style={{ backgroundColor: "#f47373" }}>
          Clear Filter
        </Button>
      </div>
      <Divider style={{ marginBottom: "10px" }} />
      <Paper sx={{ width: "100%" }}>
        <DataGrid
          columns={columns}
          density='comfortable'
          getRowId={(row) => row.asset_pool}
          rows={rows}
          {...rows}
          slots={{ toolbar: GridToolbar, pagination: CustomPagination }}
          checkboxSelection
          style={{
            backgroundColor: `${mode === "dark" ? "#101418" : "#ffffff"}`,
            color: `${mode === "dark" ? "#fff" : "#000"}`,
          }}
          sx={{
            boxShadow: 2,
            border: 1,
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-footerCell": {
              backgroundColor: "primary.main",
            },
            "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
              backgroundColor: mode === "light" ? "#fafafa" : "#1d1d1d",
              borderBottom: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              borderRight: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-cell": {
              color:
                mode === "light" ? "rgba(0,0,0,.85)" : "rgba(255,255,255,0.65)",
            },
          }}
        />
      </Paper>
    </div>
  );
}
